import { useEffect, useMemo, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'
import { useCurrentUser } from '@/features/auth/state/auth'

import { DomainConfig } from '@/config/domain'
import { useTenantContext } from '@/context/tenant'

import { CreateTenantDropdownItemProps } from '@/features/tenant/components'
import { useTenantsConfigByUser } from '@/features/tenant/services'
import { useAtom } from 'jotai'
import CreateTenantModal, { createTenantModalAtom } from '@/features/tenant/components/create-tenant-modal'
import Icon from '@/components/ui/icon'

import { useNavigate } from 'react-router-dom'
import { ROUTE_BUILDERS } from '@/config/routes'

export function TeamSwitcher() {
  const user = useCurrentUser()
  const teams = useTenantsConfigByUser()
  const { isMobile } = useSidebar()
  const [open, setOpen] = useState(false)
  const { currentTenant, selectedTenant, setSelectedTenant } = useTenantContext()
  const [isAddModalOpen, setIsAddModalOpen] = useAtom(createTenantModalAtom)

  const displayTenant = useMemo(() => {
    if (selectedTenant) {
      return selectedTenant
    }

    return currentTenant
  }, [currentTenant, selectedTenant])

  const bg = displayTenant?.theme?.['--primary-color'] ?? 'var(--primary-color)'
  const bgHover = displayTenant?.theme?.['--primary-hover'] ?? 'var(--primary-hover)'
  const fg = displayTenant?.theme?.['--text-color'] ?? 'var(--primary-foreground)'
  const logoUrl = useMemo(() => {
    // cadeia de fallback: iconBG -> icon -> null
    const candidate = displayTenant?.iconBG || displayTenant?.icon || null
    if (!candidate) return null
    // se seus ícones são relativos ao domínio do tenant:
    // return (displayTenant?.domainPath || '') + candidate
    return candidate
  }, [displayTenant])

  // controla se a imagem carregou; reseta quando o tenant mudar
  const [imgOk, setImgOk] = useState<boolean>(!!logoUrl)

  useEffect(() => {
    setImgOk(!!logoUrl)
  }, [logoUrl])

  const getTenantInitials = (title?: string) => {
    if (!title) return '—'
    const parts = title.trim().split(/\s+/).slice(0, 2)
    return parts.map(p => p[0]?.toUpperCase() ?? '').join('') || '—'
  }
  function handleOpenChange(open: boolean) {
    if (user?.typeUser === 'ADMIN') {
      setOpen(open)
    }
  }
  const navigate = useNavigate()
  // …

  function handleSelectTenant(selected: DomainConfig) {
    const tenantSelected = teams?.find(t => t.id === selected.id)

    if (tenantSelected) {
      setSelectedTenant({
        ...tenantSelected,
        ...selected
      })
      navigate(ROUTE_BUILDERS.clientList(selected.id))
    }
  }

  if (!currentTenant || !user) {
    return null
  }

  const tenantUser = {
    ...user,
    id_company: user.id_company ?? undefined
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                style={
                  {
                    ['--btn-bg' as any]: bg,
                    ['--btn-bg-hover' as any]: bgHover,
                    ['--btn-fg' as any]: fg
                  } as React.CSSProperties
                }
                className="
      group/button h-14 py-3
      bg-[var(--btn-bg)] text-[var(--btn-fg)]
      hover:bg-[var(--btn-bg-hover)]
      data-[state=open]:bg-[var(--btn-bg-hover)]
      data-[state=open]:text-[var(--btn-fg)]
              
      flex items-center gap-2
      group-data-[collapsible=icon]:justify-center
      group-data-[collapsible=icon]:px-0
    "
              >
                {/* LOGO sempre visível e central quando colapsado */}
                <div
                  className="
        size-8 shrink-0 rounded-lg overflow-hidden
        grid place-items-center
        group-data-[collapsible=icon]:mx-auto
      "
                  aria-hidden
                >
                  {imgOk && logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={displayTenant?.title ? `Logo ${displayTenant.title}` : 'Logo'}
                      className="h-full w-full object-cover"
                      onError={() => setImgOk(false)}
                    />
                  ) : (
                    <div
                      className="h-full w-full grid place-items-center text-[10px] font-semibold"
                      style={{ background: 'rgba(255,255,255,0.15)', color: 'currentColor' }}
                      title={displayTenant?.title || 'Empresa'}
                    >
                      {getTenantInitials(displayTenant?.title)}
                    </div>
                  )}
                </div>

                {/* TEXTO: some no modo colapsado */}
                <div
                  className="
      grid flex-1 text-left text-sm leading-tight min-w-0
      group-data-[collapsible=icon]:hidden
    "
                >
                  <span className="truncate font-semibold">{displayTenant?.title ?? 'Empresa'}</span>
                </div>

                {/* ÍCONE MENU: some no modo colapsado para não substituir o logo */}
                {user.typeUser === 'ADMIN' && (
                  <Icon
                    name="Menu"
                    className="
          ml-auto size-4
          group-data-[collapsible=icon]:hidden
        "
                  />
                )}
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs ">Empresas</DropdownMenuLabel>

              {teams?.map(team => (
                <DropdownMenuItem key={team.title} onClick={() => handleSelectTenant(team)} className="gap-2 p-2">
                  <div className="flex size-6 items-center justify-center rounded-sm overflow-hidden">
                    {/* mini-logo com fallback também */}
                    {team.icon ? (
                      <img
                        src={team.icon}
                        alt={`Logo ${team.title}`}
                        className="size-4 shrink-0"
                        onError={e => {
                          // some a imagem quebrada e mostra iniciais no lugar
                          const target = e.currentTarget
                          const parent = target.parentElement
                          if (parent) {
                            target.style.display = 'none'
                            const fallback = document.createElement('div')
                            fallback.className = 'size-4 grid place-items-center text-[8px] font-semibold'
                            fallback.textContent = getTenantInitials(team.title)
                            parent.appendChild(fallback)
                          }
                        }}
                      />
                    ) : (
                      <div className="size-4 grid place-items-center text-[8px] font-semibold">
                        {getTenantInitials(team.title)}
                      </div>
                    )}
                  </div>
                  {team.title}
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator />
              <CreateTenantDropdownItemProps
                user={tenantUser}
                handleCreateClick={() => {
                  setOpen(false)
                  setIsAddModalOpen(true)
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>

          <CreateTenantModal open={isAddModalOpen} onOpenChange={setIsAddModalOpen} />
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  )
}
